import { Link } from "react-router-dom";
import {
  ArrowRight, Check, HardHat, Wrench, QrCode, MessageCircle, Smartphone, Quote,
  Sun, Wind, Flame, Atom, Waves, Thermometer, Fuel, Droplets, Route, Factory, Building2, RadioTower,
} from "lucide-react";

const GOLD = "#C9A84C";
const NAVY = "#0A1628";
const CARD = "#112240";
const STRIP = "#0D1B2A";
const MUTED = "#8892B0";

const H2 = ({ children, testid }) => (
  <h2 className="font-display text-2xl sm:text-3xl font-bold text-white" data-testid={testid}>{children}</h2>
);

const Sub = ({ children }) => (
  <p className="text-[14px] mt-3 max-w-[720px] leading-relaxed" style={{ color: MUTED }}>{children}</p>
);

const MODULES = [
  {
    icon: HardHat, title: "EPC Execution", to: "/features", cta: "Explore EPC Features",
    text: "WBS planning, BOQ tracking, AI Risk Radar, Daily Progress Reports, S-Curve analytics, Earned Value Management, hindrance tracking, resource planning, and critical path analysis.",
    tags: ["WBS", "BOQ", "Risk Radar", "DPR", "EVM", "Critical Path"],
  },
  {
    icon: Wrench, title: "Operations and Maintenance", to: "/om/sectors", cta: "Explore O&M Features",
    text: "Asset register, daily monitoring logs, preventive and corrective maintenance, spare parts inventory, MTBF/MTTR reliability analytics, automated RCA engine, and branded compliance reports.",
    tags: ["Asset Register", "PM Scheduling", "Fault Ticketing", "MTBF/MTTR", "RCA Engine", "QR Scanning"],
  },
];

const SECTOR_TILES = [
  [Sun, "Solar Power", "solar-power"], [Wind, "Wind Energy", "wind-energy"],
  [Flame, "Thermal Power", "thermal-power-plant"], [Atom, "Nuclear Power", "nuclear-power-plant"],
  [Waves, "Hydropower", "hydropower-plant"], [Thermometer, "HVAC and MEP", "hvac-and-mep-systems"],
  [Fuel, "Oil and Gas", "oil-and-gas"], [Droplets, "Water Treatment", "water-treatment-plant"],
  [Route, "Roads and Highways", "roads-and-highways"],
  [Factory, "Industrial Plants", "industrial-and-manufacturing-plant"],
  [Building2, "Civil Infrastructure", ""], [RadioTower, "Telecom and Towers", ""],
];

const NUMBERS = [
  ["10+", "Sectors Supported", "Solar to nuclear, EPC to O&M"],
  ["100+", "O&M Asset Categories", "Pre-loaded equipment templates"],
  ["50+", "Pre-built RCA Fault Templates", "5-Why chains ready to use"],
  ["14-Day", "Free Trial, No Credit Card", "Full EPC and O&M access"],
];

const FLOW = [
  ["Project Setup", "Define WBS, upload BOQ, assign teams, and set baselines."],
  ["EPC Execution", "Track daily progress, monitor costs, manage risks, and log hindrances."],
  ["Handover", "Generate handover reports, register all assets, and transition to O&M."],
  ["Operations and Maintenance", "Monitor assets daily, schedule PM, resolve faults, and generate compliance reports."],
];

const FIELD = [
  [QrCode, "Scan. Log. Done.", "Every asset has a unique QR code. Technicians scan it on-site and log daily readings or raise fault tickets in under 30 seconds — directly from their phone camera."],
  [MessageCircle, "Instant WhatsApp Alerts.", "Critical faults and overdue PM tasks trigger immediate WhatsApp notifications to the O&M Manager — no app needed on the recipient's end."],
  [Smartphone, "Works on Any Phone.", "The O&M field interface is lightweight and mobile-optimised — loads in under 3 seconds on 4G. No app installation required."],
];

const VOICES = [
  ["Plan-Sarthi's EVM and Risk Radar gave us early warning on a cost overrun that would have blown our contingency. We corrected course 6 weeks before it became a crisis.", "Project Manager", "132kV Substation EPC", "Uttar Pradesh"],
  ["The O&M module replaced 4 different Excel files our team was maintaining manually. PM compliance went from 62% to 91% in the first two months.", "O&M Manager", "50MW Solar Plant", "Rajasthan"],
  ["We submitted our NHAI quarterly O&M report directly from Plan-Sarthi for the first time. What used to take 3 days of data collection took 20 minutes.", "O&M Concessionaire", "4-Lane Highway Package", "Maharashtra"],
];

export const TwoModules = () => (
  <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="home-two-modules">
    <H2 testid="two-modules-heading">One Platform. Two Powerful Modules.</H2>
    <Sub>
      Plan-Sarthi combines EPC project execution and post-handover O&amp;M in a single platform — no switching
      tools, no data loss between construction and operations.
    </Sub>
    <div className="grid lg:grid-cols-2 gap-4 mt-10">
      {MODULES.map(({ icon: Icon, title, text, tags, to, cta }) => (
        <div key={title} className="p-6 sm:p-7 rounded-lg border transition-transform duration-200 hover:-translate-y-1"
             style={{ background: CARD, borderColor: "rgba(255,255,255,0.1)", borderLeft: `3px solid ${GOLD}` }}
             data-testid={`module-card-${title.split(" ")[0].toLowerCase()}`}>
          <Icon className="w-7 h-7" style={{ color: GOLD }} />
          <h3 className="font-semibold text-[20px] text-white mt-4">{title}</h3>
          <p className="text-[13.5px] mt-3 leading-relaxed" style={{ color: MUTED }}>{text}</p>
          <div className="flex flex-wrap gap-1.5 mt-5">
            {tags.map((t) => (
              <span key={t} className="px-2.5 py-1 text-[11.5px] rounded-full"
                    style={{ background: NAVY, color: GOLD, border: "1px solid rgba(201,168,76,0.28)" }}>{t}</span>
            ))}
          </div>
          <Link to={to} className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold mt-6 hover:underline"
                style={{ color: GOLD }} data-testid={`module-cta-${title.split(" ")[0].toLowerCase()}`}>
            {cta} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ))}
    </div>
  </section>
);

export const SectorStrip = () => (
  <section className="border-y" style={{ background: CARD, borderColor: "rgba(255,255,255,0.1)" }} data-testid="home-sector-strip">
    <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20">
      <H2 testid="sector-strip-heading">Built for Every Indian Infrastructure Sector</H2>
      <Sub>
        From 500MW solar farms to nuclear containment structures — Plan-Sarthi is designed for the complexity
        and scale of Indian EPC projects.
      </Sub>
      <div className="mt-10 -mx-5 px-5 sm:mx-0 sm:px-0 overflow-x-auto sector-scroll" data-testid="sector-tiles-scroll">
        <div className="flex gap-3 pb-3 sm:grid sm:grid-cols-4 lg:grid-cols-6">
          {SECTOR_TILES.map(([Icon, name, slug]) => {
            const inner = (
              <>
                <Icon className="w-6 h-6" style={{ color: GOLD }} />
                <span className="text-[12.5px] text-white leading-tight text-center">{name}</span>
              </>
            );
            const cls = "shrink-0 w-[132px] sm:w-auto flex flex-col items-center gap-2.5 rounded-lg border p-4 transition-colors duration-200";
            const style = { background: NAVY, borderColor: "rgba(255,255,255,0.1)" };
            return slug ? (
              <Link key={name} to={`/om/sectors/${slug}`} className={`${cls} hover:border-[#C9A84C]`} style={style}
                    data-testid={`sector-tile-${slug}`}>{inner}</Link>
            ) : (
              <div key={name} className={cls} style={style} data-testid={`sector-tile-${name.split(" ")[0].toLowerCase()}`}>{inner}</div>
            );
          })}
        </div>
      </div>
      <p className="text-[12.5px] text-center mt-6" style={{ color: MUTED }}>
        Sector-specific O&amp;M templates, compliance checklists, and dashboard KPIs pre-loaded for each industry.
      </p>
    </div>
  </section>
);

export const PlatformNumbers = () => (
  <section style={{ background: STRIP }} data-testid="home-platform-numbers">
    <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-14 grid grid-cols-2 lg:grid-cols-4">
      {NUMBERS.map(([value, label, sub], i) => (
        <div key={label} className="px-2 sm:px-6 py-4 lg:py-0"
             style={{ borderLeft: i === 0 ? "none" : `1px solid rgba(201,168,76,0.22)` }}
             data-testid={`platform-number-${i}`}>
          <div className="font-display font-bold leading-none num text-[34px] sm:text-[44px] lg:text-[48px]" style={{ color: GOLD }}>{value}</div>
          <p className="text-[14px] sm:text-[16px] text-white mt-2.5">{label}</p>
          <p className="text-[12px] mt-1" style={{ color: MUTED }}>{sub}</p>
        </div>
      ))}
    </div>
  </section>
);

export const EpcToOm = () => (
  <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="home-epc-to-om">
    <H2 testid="epc-to-om-heading">From First Weld to Final Inspection — One Continuous Workflow</H2>
    <Sub>
      Plan-Sarthi follows your project from construction kickoff through handover and into years of O&amp;M
      operations.
    </Sub>
    <div className="mt-10 relative">
      <span className="md:hidden absolute left-[19px] top-4 bottom-4 w-px" style={{ background: "rgba(201,168,76,0.35)" }} />
      <div className="grid md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-5 md:gap-4 items-stretch">
        {FLOW.map(([title, text], i) => (
          <div key={title} className="contents">
            <div className="relative rounded-lg border p-5 pl-14 md:pl-5"
                 style={{ background: CARD, borderColor: "rgba(255,255,255,0.1)" }} data-testid={`flow-step-${i + 1}`}>
              <span className="absolute left-2.5 top-5 md:static flex items-center justify-center w-[34px] h-[34px] rounded-full font-display font-bold text-[15px] md:mb-3"
                    style={{ background: GOLD, color: NAVY }}>{i + 1}</span>
              <h3 className="font-semibold text-[16px] text-white">{title}</h3>
              <p className="text-[13px] mt-2 leading-relaxed" style={{ color: MUTED }}>{text}</p>
            </div>
            {i < FLOW.length - 1 ? (
              <div className="hidden md:flex items-center justify-center">
                <ArrowRight className="w-5 h-5" style={{ color: "rgba(201,168,76,0.6)" }} />
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const FieldFeatures = () => (
  <section className="border-y" style={{ background: CARD, borderColor: "rgba(255,255,255,0.1)" }} data-testid="home-field-features">
    <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20">
      <H2 testid="field-features-heading">Built for the Field, Not Just the Office</H2>
      <Sub>
        Site technicians can log readings, raise faults, and scan QR codes directly from their phone — no
        laptop, no paperwork.
      </Sub>
      <div className="grid md:grid-cols-3 gap-4 mt-10">
        {FIELD.map(([Icon, title, text], i) => (
          <div key={title} className="rounded-lg border p-6 transition-transform duration-200 hover:-translate-y-1"
               style={{ background: NAVY, borderColor: "rgba(255,255,255,0.1)" }} data-testid={`field-card-${i}`}>
            <Icon className="w-7 h-7" style={{ color: GOLD }} />
            <h3 className="font-semibold text-[16px] text-white mt-4">{title}</h3>
            <p className="text-[13px] mt-2.5 leading-relaxed" style={{ color: MUTED }}>{text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const FieldVoices = () => (
  <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="home-field-voices">
    <H2 testid="field-voices-heading">Trusted by EPC and O&amp;M Teams Across India</H2>
    <div className="grid md:grid-cols-3 gap-4 mt-10">
      {VOICES.map(([quote, role, project, state], i) => (
        <div key={role} className="rounded-lg border p-6 flex flex-col"
             style={{ background: CARD, borderColor: "rgba(255,255,255,0.1)" }} data-testid={`om-testimonial-${i}`}>
          <Quote className="w-8 h-8" style={{ color: GOLD, opacity: 0.55 }} />
          <p className="text-[15px] text-white mt-3 flex-1" style={{ lineHeight: 1.7 }}>{quote}</p>
          <div className="mt-5 pt-4 border-t text-[12.5px]" style={{ borderColor: "rgba(255,255,255,0.1)", color: MUTED }}>
            {role} · {project} · {state}
          </div>
        </div>
      ))}
    </div>
    <p className="text-[11px] text-center mt-6" style={{ color: MUTED, opacity: 0.7 }}>
      Illustrative accounts from early access engagements. Named references available on request.
    </p>
  </section>
);

export const PricingCta = () => (
  <section style={{ background: NAVY, borderTop: `2px solid ${GOLD}`, borderBottom: `2px solid ${GOLD}` }} data-testid="home-pricing-cta">
    <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-14 text-center">
      <h2 className="font-display text-2xl sm:text-[28px] font-bold text-white">
        Start with a 14-Day Free Trial — No Credit Card Required
      </h2>
      <p className="text-[15px] sm:text-[16px] mt-3" style={{ color: MUTED }}>
        Full access to EPC and O&amp;M features. Upgrade to Professional or Enterprise when you are ready.
      </p>
      <div className="flex flex-wrap justify-center gap-3 mt-8">
        <Link to="/signup" className="inline-flex items-center h-12 px-8 rounded-lg text-[15px] font-bold transition-transform duration-200 hover:-translate-y-0.5"
              style={{ background: GOLD, color: NAVY }} data-testid="pricing-cta-trial-btn">
          Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
        <Link to="/pricing" className="inline-flex items-center h-12 px-8 rounded-lg border text-[15px] font-semibold transition-colors duration-200 hover:bg-[#C9A84C]/10"
              style={{ borderColor: GOLD, color: GOLD }} data-testid="pricing-cta-pricing-btn">
          View Pricing
        </Link>
      </div>
      <div className="flex flex-wrap justify-center gap-x-7 gap-y-2 mt-8 text-[12.5px]" style={{ color: MUTED }}>
        {["Secure Indian data hosting", "Razorpay payments in INR", "Cancel anytime."].map((t) => (
          <span key={t} className="inline-flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5" style={{ color: GOLD }} /> {t}
          </span>
        ))}
      </div>
    </div>
  </section>
);
